import * as _ from 'lodash';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

import { useAddToast } from '../contexts/ToastContext';

import { type ErrorResponse, getErrorMessage } from './errors';
import useMutation, { type EmptyMutationResponse } from './useMutation';

type SuccessMessageFn<TData, TVariables> = (data: TData, variables: TVariables) => string;
type ErrorMessageFn<TError, TVariables> = (error: TError, variables: TVariables) => string;

export type ToastedMutationOptions<TData, TError, TVariables, TContext = unknown> =
  UseMutationOptions<TData, TError, TVariables, TContext> & {
  /** Success message to display as a toast. Can be a string or a function that returns a string */
  successToastMessage?: string | SuccessMessageFn<TData, TVariables>;
  /** Error message to display as a toast. Can be a string or a function that returns a string */
  errorToastMessage?: string | ErrorMessageFn<TError, TVariables>;

};

/**
 * This hook is a wrapper around our useMutation hook which handles
 * a common pattern, which is to display a success toast when the mutation
 * succeeds, and an error toast when it fails.
 *
 * 🍞 Cheers to [lwbrown42](https://github.com/lwbrown42) for the name.
 *
 * @param {ToastedMutationOptions<TData, TError, TVariables>} options - The mutation options, including success/error toast messages
 * @returns {UseMutationResult<TData, TError, TVariables>} The wrapped mutation hook
 *
 * @example
 * // Basic usage
 * const { mutate } = useToastedMutation({
 *   mutationFn: createUser,
 *   successMessage: "User created successfully",
 *   errorMessage: "Failed to create user"
 * });
 *
 * @example
 * // With additional callbacks
 * const { mutate } = useToastedMutation({
 *   mutationFn: createUser,
 *   successMessage: (data) => `User ${data.name} created successfully`,
 *   errorMessage: "Failed to create user",
 *   onSuccess: () => {
 *     // Additional success handling
 *     queryClient.invalidateQueries(['users']);
 *   }
 * });
 */
export default function useToastedMutation<
  TData = EmptyMutationResponse,
  TError = ErrorResponse,
  TVariables = void,
  TContext = unknown,
>(
  options: ToastedMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const addToast = useAddToast();
  const { mutationFn, successToastMessage, errorToastMessage, onSuccess, onError, ...restOptions } = options;

  return useMutation({
    ...restOptions,
    mutationFn,
    onSuccess: (data, variables, context) => {
      //  Both of the toast messages are optional, so we need to check if they are provided
      //  This allows us to (for example) display a success toast without an error toast
      if (successToastMessage) {
        // Display success toast
        const message = _.isFunction(successToastMessage)
          ? successToastMessage(data, variables)
          : successToastMessage;

        addToast({
          type: 'success',
          content: message,
        });
      }

      // Call the original onSuccess if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      //  Both of the toast messages are optional, so we need to check if they are provided
      //  This allows us to (for example) display an error toast without a success toast
      if (errorToastMessage) {
        // Generate error message
        const fallbackMessage = _.isFunction(errorToastMessage)
          ? errorToastMessage(error, variables)
          : errorToastMessage;

        addToast({
          type: 'error',
          content: getErrorMessage(error, fallbackMessage),
        });
      }

      // Call the original onError if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
}
